import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiOutlineChevronDown } from "react-icons/hi2";
import useCareLabels from "hooks/useCareLabels";
import {
  ADVANCE_RATIO_SIDE_MAX,
  DEFAULT_ADVANCE_RATIO,
  formatAdvanceRatio,
  parseAdvanceRatioParts,
} from "./advanceModeSplitDefaults";
import "./advanceModeTimePicker.css";

const ADVANCE_RATIO_MENU_Z_INDEX = 100000;

const SIDES = Array.from({ length: ADVANCE_RATIO_SIDE_MAX }, (_, i) => i + 1);
const MENU_MIN_WIDTH = 228;

const sideButtonClass = (isActive) =>
  `flex w-full items-center px-3 py-1.5 text-left text-xs tabular-nums transition-colors ${
    isActive
      ? "bg-sky-50 font-semibold text-sky-700"
      : "text-slate-700 hover:bg-slate-50"
  }`;

/**
 * Nested ratio picker: worker 1–10 in the first column, participant 1–10 in
 * the second (e.g. 2:4). Portalled so it is not clipped by advance-mode scroll.
 */
const AdvanceModeRatioSelect = ({
  value,
  onChange,
  disabled = false,
  className = "",
}) => {
  const { participantLabel } = useCareLabels();
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const displayValue = value || DEFAULT_ADVANCE_RATIO;
  const { left, right } = parseAdvanceRatioParts(displayValue);
  const pickerLeft =
    left >= 1 && left <= ADVANCE_RATIO_SIDE_MAX ? left : 1;
  const pickerRight =
    right >= 1 && right <= ADVANCE_RATIO_SIDE_MAX ? right : 1;

  const updatePosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.max(rect.width, MENU_MIN_WIDTH);
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 280 && rect.top > spaceBelow;
    setMenuStyle({
      position: "fixed",
      left: Math.min(rect.left, maxLeft),
      width,
      zIndex: ADVANCE_RATIO_MENU_Z_INDEX,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const close = (event) => {
      const target = event.target;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onScroll = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, updatePosition]);

  const apply = (nextLeft, nextRight) => {
    onChange?.(formatAdvanceRatio(nextLeft, nextRight));
  };

  return (
    <div className={`advance-mode-ratio-select ${className}`.trim()}>
      <button
        ref={buttonRef}
        type='button'
        disabled={disabled}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label={`Ratio ${displayValue}`}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        className={`advance-mode-ratio-control${open ? " is-open" : ""}`}
      >
        <span className='advance-mode-ratio-control__value'>{displayValue}</span>
        <HiOutlineChevronDown
          className={`advance-mode-ratio-control__chevron${open ? " is-open" : ""}`}
          aria-hidden
        />
      </button>
      {open &&
        !disabled &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className='advance-mode-ratio-menu'
            role='listbox'
          >
            <div className='grid grid-cols-2 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500'>
              <span>Worker</span>
              <span>{participantLabel || "Participant"}</span>
            </div>
            <div className='grid grid-cols-2'>
              <div className='max-h-56 overflow-y-auto border-r border-slate-100 py-1'>
                {SIDES.map((n) => (
                  <button
                    key={`worker-${n}`}
                    type='button'
                    role='option'
                    aria-selected={n === pickerLeft}
                    className={sideButtonClass(n === pickerLeft)}
                    onClick={() => apply(n, pickerRight)}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className='max-h-56 overflow-y-auto py-1'>
                {SIDES.map((n) => {
                  const ratio = formatAdvanceRatio(pickerLeft, n);
                  return (
                    <button
                      key={`participant-${n}`}
                      type='button'
                      role='option'
                      aria-selected={n === pickerRight}
                      className={sideButtonClass(n === pickerRight)}
                      onClick={() => {
                        apply(pickerLeft, n);
                        setOpen(false);
                      }}
                    >
                      {ratio}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className='border-t border-slate-100 px-3 py-1.5 text-[10px] text-slate-500'>
              Worker : {String(participantLabel || "Participant").toLowerCase()} ·{" "}
              {displayValue}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AdvanceModeRatioSelect;
